import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'
import { DeepPartial } from 'utility-types'
import { l } from '@manychat/manyui'
import { PlanSlug } from '@manychat/paywall'

import { AiAutomationModel } from 'apps/aiAutomation/store/core/lib/model'
import { AiAutomationCoreState, LoaderStatus } from 'apps/aiAutomation/store/core/lib/types'
import { billing, UpgradeSource } from 'common/billing'
import { alert } from 'common/core'
import { isProOrTrialStatus } from 'common/core/models/Account/helpers'
import { getAccountStatus, getIsAccountFlagEnabled } from 'common/core/selectors/appSelectors'
import { AiAutomationApi } from 'shared/api/requests/aiAutomation'
import {
  AiAutomationConfig,
  FallbackAssignee,
  AiAutomationScenario,
  TovSample,
  AiAutomationTovState,
} from 'shared/api/requests/aiAutomation/schemas'
import { AiToneOfVoiceApi } from 'shared/api/requests/aiToneOfVoice'
import { TovReaction } from 'shared/api/requests/aiToneOfVoice/schemas'
import { createAsyncAction } from 'shared/lib/redux'
import { getClientId } from 'utils/clientId'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import {
  accountNotificationsListener,
  anotherTabNotificationsListener,
} from 'utils/services/notificationsService'

const NAMESPACE = 'aiAutomationCore'

export const AiAutomationCoreInitialState: AiAutomationCoreState = {
  config: {
    business_context: [],
    scenarios: {
      faq: {
        published: false,
        fallback_assignee: {
          group_id: null,
          user_id: null,
        },
      },
      social_engagement: {
        published: false,
        tone_of_voice_state: null,
        tone_of_voice_data: [],
      },
    },
  },
  loaderStatus: LoaderStatus.NOT_LOADED,
}

const { reducer, actions } = createSlice({
  initialState: AiAutomationCoreInitialState,
  name: NAMESPACE,
  reducers: {
    configUpdated: (state, action: PayloadAction<AiAutomationConfig>) => {
      state.config = action.payload
    },
    tovStateUpdated: (state, action: PayloadAction<AiAutomationTovState>) => {
      state.config.scenarios.social_engagement.tone_of_voice_state = action.payload
    },
    tovGenerationDataUpdated: (state, action: PayloadAction<TovSample[]>) => {
      state.config.scenarios.social_engagement.tone_of_voice_data = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConfigFx.pending, (state) => {
      state.loaderStatus = LoaderStatus.DASHBOARD_LOADING
    })
    builder.addCase(getConfigFx.fulfilled, (state, action) => {
      state.config = action.payload
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateConfigFx.fulfilled, (state, action) => {
      state.config = action.payload
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateConfigFx.rejected, (state) => {
      state.loaderStatus = LoaderStatus.LOADED
    })

    builder.addCase(updateScenarioStatus.pending, (state, action) => {
      state.loaderStatus =
        action.meta.arg.scenario === AiAutomationScenario.INSTANT_ANSWERS
          ? LoaderStatus.INSTANT_ANSWERS_STATUS_UPDATING
          : LoaderStatus.SOCIAL_ENGAGEMENT_STATUS_UPDATING
    })

    builder.addCase(updateScenarioStatus.fulfilled, (state) => {
      state.loaderStatus = LoaderStatus.LOADED
    })
  },
})

const getConfigFx = createAsyncAction(`${NAMESPACE}/getConfigFx`, async () => {
  const { data } = await AiAutomationApi.getConfig()

  return data.config
})

const updateConfigFx = createAsyncAction(
  `${NAMESPACE}/updateConfigFx`,
  async (changes: DeepPartial<AiAutomationConfig>, { getState }) => {
    const isNewKnowledgeEnabled = getIsAccountFlagEnabled(
      getState(),
      AccountFlag.ENABLE_AI_AUTOMATION_KNOWLEDGE_NEW_API,
    )

    const config = AiAlwaysOnAutomationsSelectors.getConfig(getState())

    const { data } = await AiAutomationApi.updateConfig({
      query: { client_id: getClientId() },
      body: {
        config: AiAutomationModel.applyChangesToConfig(config, changes, isNewKnowledgeEnabled),
      },
    })

    return data.config
  },
)

const updateScenarioStatus = createAsyncAction(
  `${NAMESPACE}/updateScenarioStatus`,
  async (
    { status, scenario }: { status: boolean; scenario: AiAutomationScenario },
    { dispatch, getState },
  ) => {
    const accountStatus = getAccountStatus(getState())

    if (status && !isProOrTrialStatus(accountStatus)) {
      billing.proAlert({
        products: [PlanSlug.AUTOMATION],
        source: UpgradeSource.AI_AUTOMATION_PUBLISH,
      })
      return
    }

    await dispatch(
      updateConfigFx({
        scenarios: {
          [scenario]: {
            published: status,
          },
        },
      }),
    ).unwrap()

    alert(
      status
        ? l.translate('Your automation is live')
        : l.translate('Your automation has been stopped'),
      'success',
    )
  },
)

const updateFallbackAssigneeFx = createAsyncAction(
  `${NAMESPACE}/updateFallbackAssigneeFx`,
  async (fallback_assignee: FallbackAssignee, { dispatch }) => {
    await dispatch(
      updateConfigFx({
        scenarios: {
          faq: {
            fallback_assignee,
          },
        },
      }),
    )
  },
)

const startTovSetupFx = createAsyncAction(
  `${NAMESPACE}/startTovSetupFx`,
  async (_, { dispatch }) => {
    dispatch(actions.tovStateUpdated(AiAutomationTovState.GENERATION_IN_PROGRESS))

    AiToneOfVoiceApi.startSetup()
  },
)

const saveUserTovReactionFx = createAsyncAction(
  `${NAMESPACE}/saveUserTovReactionFx`,
  async (answers: TovReaction[], { dispatch }) => {
    await AiToneOfVoiceApi.saveReaction({
      body: {
        replies_with_feedback: answers,
      },
    })

    dispatch(actions.tovStateUpdated(AiAutomationTovState.ACTIVE))
  },
)

anotherTabNotificationsListener.on(
  'ai_automation_updated',
  (data: { config: AiAutomationConfig }, dispatch) => {
    dispatch(actions.configUpdated(data.config))
  },
)

accountNotificationsListener.on(
  'ai_automation_positive_reply_samples_generated',
  (data: { samples: TovSample[] }, dispatch) => {
    dispatch(actions.tovGenerationDataUpdated(data.samples))
    dispatch(actions.tovStateUpdated(AiAutomationTovState.WAITING_USER_REACTION))
  },
)

export const AiAlwaysOnAutomationsActions = {
  configUpdated: actions.configUpdated,

  updateScenarioStatus,
  updateFallbackAssigneeFx,
  getConfigFx,
  updateConfigFx,

  startTovSetupFx,
  saveUserTovReactionFx,
}

export const AiAlwaysOnAutomationsReducer = reducer

const getState = (state: RootState) => state.aiAutomation.core

export const AiAlwaysOnAutomationsSelectors = {
  getState,
  getFaqScenarioStatus: (state: RootState) => getState(state).config.scenarios.faq.published,
  getIsSocialEngagementPublished: (state: RootState) => {
    return getState(state).config.scenarios.social_engagement.published
  },
  getConfig: (state: RootState) => getState(state).config,
  getLoaderStatus: (state: RootState) => getState(state).loaderStatus,
  getIsLoaded: (state: RootState) =>
    ![LoaderStatus.DASHBOARD_LOADING, LoaderStatus.NOT_LOADED].includes(
      getState(state).loaderStatus,
    ),
  getFallbackAssignee: createSelector(
    [(state) => getState(state).config.scenarios.faq.fallback_assignee],
    (assignee) => assignee?.user_id ?? assignee?.group_id ?? null,
  ),
  getTovState: (state: RootState) =>
    getState(state).config.scenarios.social_engagement.tone_of_voice_state,
  getTovSamples: createSelector(
    [(state: RootState) => getState(state).config.scenarios.social_engagement.tone_of_voice_data],
    (samples) => samples ?? [],
  ),
}
